import React, { useEffect, useState } from 'react';
import { CalendarClock, Flag, GraduationCap, Trophy, Utensils, Tent, CreditCard, Landmark, ChevronRight, CalendarPlus, XCircle, Loader2, Clock, History } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, isToday, isTomorrow, isThisYear } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GREEN = '#2E6A4C';

const KIND = {
  depart: { icon: Flag, color: '#2E6A4C', bg: '#E8F3EC', label: 'Départ' },
  cours: { icon: GraduationCap, color: '#1D4ED8', bg: '#E8EEFB', label: 'Cours' },
  stage: { icon: Tent, color: '#7C3AED', bg: '#F0EAFB', label: 'Stage' },
  competition: { icon: Trophy, color: '#C2410C', bg: '#FDEEE4', label: 'Compétition' },
  restaurant: { icon: Utensils, color: '#B45309', bg: '#FBF1DF', label: 'Restaurant' },
  cotisation: { icon: CreditCard, color: '#B91C1C', bg: '#FBE7E7', label: 'Échéance' },
  sepa: { icon: Landmark, color: '#475569', bg: '#EEF1F5', label: 'Prélèvement' },
};

const dayLabel = (d) => {
  const dt = parseISO(d);
  if (isToday(dt)) return "Aujourd'hui";
  if (isTomorrow(dt)) return 'Demain';
  return format(dt, isThisYear(dt) ? 'EEE d MMM' : 'EEE d MMM yyyy', { locale: fr });
};

/* Accueil → « Mes prochains rendez-vous » : tout ce qui est réservé ou à échéance, au même endroit (+ historique). */
export const UpcomingCard = ({ token, onNavigate, onCancelled, onCancelRequest, onReschedule, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [past, setPast] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [busyKey, setBusyKey] = useState('');
  const [confirmKey, setConfirmKey] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const load = (p = past) => fetch(`${API_URL}/api/client-portal/upcoming?limit=30${p ? '&past=1' : ''}`, { headers })
    .then(r => (r.ok ? r.json() : { items: [] })).then(setData).catch(() => setData({ items: [] }));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token, refreshKey, past]);

  const switchMode = (p) => { if (p !== past) { setPast(p); setShowAll(false); setConfirmKey(''); } };
  const keyOf = (it) => `${it.kind}-${it.id}`;

  const cancelItem = async (it) => {
    setBusyKey(keyOf(it));
    try {
      const r = await fetch(`${API_URL}${it.cancel.url}`, { method: it.cancel.method || 'DELETE', headers });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || 'Annulation impossible');
      const credit = j.refund?.amount ? ` · Avoir de ${Number(j.refund.amount).toFixed(2).replace('.', ',')} € crédité sur votre compte` : '';
      toast.success((j.message || 'Réservation annulée') + credit);
      setConfirmKey('');
      onCancelled?.(it);
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusyKey(''); }
  };

  const addToCalendar = async (it) => {
    setBusyKey(`ics-${keyOf(it)}`);
    try {
      const r = await fetch(`${API_URL}/api/client-portal/upcoming/ics?kind=${encodeURIComponent(it.kind)}&id=${encodeURIComponent(it.id)}`, { headers });
      if (!r.ok) throw new Error('Fichier agenda indisponible');
      const blob = await r.blob();
      const url = URL.createObjectURL(new Blob([blob], { type: 'text/calendar;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${it.kind}-${it.date}.ics`; a.rel = 'noopener';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success('Événement prêt : ouvrez-le pour l\'ajouter à votre agenda');
    } catch (e) { toast.error(e.message); } finally { setBusyKey(''); }
  };

  if (!data) return null;
  const items = data.items || [];
  const visible = showAll ? items : items.slice(0, 4);
  const modeBtn = (active) => ({ backgroundColor: active ? GREEN : 'transparent', color: active ? '#fff' : '#777' });

  return (
    <div className="rounded-2xl p-4" style={{ backgroundColor: 'rgba(255,255,255,0.92)', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }} data-testid="upcoming-card">
      <div className="flex items-center justify-between mb-2.5 gap-2">
        <h4 className="font-bold uppercase flex items-center gap-1.5" style={{ fontSize: '11px', color: '#999', letterSpacing: '0.08em' }}>
          {past ? <History size={12} /> : <CalendarClock size={12} />} {past ? 'Mes réservations passées' : 'Mes prochains rendez-vous'}
        </h4>
        <div className="flex items-center gap-1.5">
          {items.length > 0 && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F0EDE8', color: '#666' }} data-testid="upcoming-count">{items.length}</span>}
          <div className="flex rounded-full p-0.5" style={{ backgroundColor: '#F0EDE8' }} role="tablist" data-testid="upcoming-mode-toggle">
            <button type="button" onClick={() => switchMode(false)} className="text-[10px] font-semibold px-2 py-0.5 rounded-full transition-colors" style={modeBtn(!past)} data-testid="upcoming-mode-upcoming">À venir</button>
            <button type="button" onClick={() => switchMode(true)} className="text-[10px] font-semibold px-2 py-0.5 rounded-full transition-colors" style={modeBtn(past)} data-testid="upcoming-mode-past">Passées</button>
          </div>
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-neutral-500 italic" data-testid="upcoming-empty">
          {past ? 'Aucune réservation passée sur les 12 derniers mois.' : 'Aucune réservation à venir. Réservez un départ, un cours ou une table ci-dessous.'}
        </p>
      ) : (
        <div className="space-y-1.5">
          {visible.map((it, i) => {
            const k = KIND[it.kind] || KIND.depart;
            const Icon = k.icon;
            const key = keyOf(it);
            const canCalendar = !past && !['cotisation', 'sepa'].includes(it.kind);
            const confirming = confirmKey === key;
            return (
              <div key={`${key}-${i}`} className="rounded-xl hover:bg-neutral-50 transition-colors" data-testid={`upcoming-item-${it.kind}-${i}`}>
                <div className="flex items-center gap-3 px-2.5 py-2">
                  <button type="button" onClick={() => onNavigate?.(it)} className="flex items-center gap-3 min-w-0 flex-1 text-left" data-testid={`upcoming-open-${it.kind}-${i}`}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: k.bg, opacity: past ? 0.7 : 1 }}>
                      <Icon size={16} style={{ color: k.color }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-semibold text-neutral-900 truncate">{it.title}</p>
                        {it.badge && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: k.bg, color: k.color }}>{it.badge}</span>}
                      </div>
                      <p className="text-[11px] text-neutral-500 truncate">
                        <span className="font-medium capitalize" style={{ color: past ? '#888' : k.color }}>{dayLabel(it.date)}{it.time ? ` · ${it.time}` : ''}</span>
                        {it.subtitle ? ` · ${it.subtitle}` : ''}
                      </p>
                    </div>
                  </button>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    {canCalendar && (
                      <button type="button" onClick={() => addToCalendar(it)} disabled={busyKey === `ics-${key}`} className="p-1.5 rounded-lg hover:bg-emerald-50" title="Ajouter à mon agenda" data-testid={`upcoming-calendar-${it.kind}-${i}`}>
                        {busyKey === `ics-${key}` ? <Loader2 size={15} className="animate-spin text-neutral-400" /> : <CalendarPlus size={15} style={{ color: GREEN }} />}
                      </button>
                    )}
                    {!past && it.kind === 'depart' && it.reschedule && onReschedule && (
                      <button type="button" onClick={() => onReschedule(it)} className="p-1.5 rounded-lg hover:bg-blue-50" title="Changer l'horaire de ce départ" data-testid={`upcoming-reschedule-${it.kind}-${i}`}>
                        <Clock size={15} className="text-blue-600" />
                      </button>
                    )}
                    {!past && it.cancel && (
                      <button
                        type="button"
                        onClick={() => (it.cancel.requires_choice && onCancelRequest ? onCancelRequest(it) : setConfirmKey(confirming ? '' : key))}
                        className="p-1.5 rounded-lg hover:bg-red-50"
                        title={it.cancel.requires_choice ? `${it.cancel.label} (avoir ou remboursement)` : it.cancel.label}
                        data-testid={`upcoming-cancel-${it.kind}-${i}`}
                      >
                        <XCircle size={15} className={confirming ? 'text-red-600' : 'text-neutral-400'} />
                      </button>
                    )}
                    {!past && !it.cancel && it.deadline_passed && (
                      <span className="text-[9px] text-amber-600 font-medium whitespace-nowrap" title={it.hours_before ? `Annulation en ligne possible jusqu'à ${it.hours_before}h avant — contactez l'accueil` : "Date limite d'inscription dépassée — contactez l'accueil pour annuler"} data-testid={`upcoming-deadline-${it.kind}-${i}`}>Délai dépassé</span>
                    )}
                    {(past || (!it.cancel && !canCalendar)) && <ChevronRight size={14} className="text-neutral-300" />}
                  </div>
                </div>
                {confirming && (
                  <div className="mx-2.5 mb-2 rounded-lg bg-red-50 border border-red-100 px-3 py-2 flex items-center justify-between gap-2" data-testid={`upcoming-cancel-confirm-${it.kind}-${i}`}>
                    <div className="min-w-0">
                      <p className="text-[11px] text-red-700 font-medium">{it.cancel.label} du {dayLabel(it.date).toLowerCase()}{it.time ? ` à ${it.time}` : ''} ?</p>
                      {it.cancel.refund_hint && <p className="text-[10px] text-red-600/80 mt-0.5" data-testid={`upcoming-cancel-refund-hint-${it.kind}-${i}`}>{it.cancel.refund_hint}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button type="button" onClick={() => setConfirmKey('')} className="text-[11px] px-2 py-1 rounded-md text-neutral-600 hover:bg-white" data-testid={`upcoming-cancel-no-${it.kind}-${i}`}>Non</button>
                      <button type="button" onClick={() => cancelItem(it)} disabled={busyKey === key} className="text-[11px] px-2.5 py-1 rounded-md bg-red-600 text-white font-semibold disabled:opacity-50" data-testid={`upcoming-cancel-yes-${it.kind}-${i}`}>
                        {busyKey === key ? '…' : 'Oui, annuler'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {items.length > 4 && (
            <button type="button" onClick={() => setShowAll(v => !v)} className="w-full text-center text-[11px] font-semibold py-1" style={{ color: GREEN }} data-testid="upcoming-toggle-all">
              {showAll ? 'Réduire' : `Voir les ${items.length - 4} autres`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
